import { type FirebaseApp, initializeApp } from "firebase/app";
import { type Auth, browserLocalPersistence, getAuth, setPersistence } from "firebase/auth";

/**
 * Firebase, for identity only.
 *
 * Firebase says who somebody is. It does not say what they can see: the API
 * takes the ID token, verifies it against the project, and resolves it to an
 * app_user row that decides the rest. That split is why there is no Firestore
 * here and no data access of any kind.
 *
 * None of these values are secret. A Firebase web config is designed to ship
 * in the bundle; the security comes from the token being signed by Google and
 * verified by the backend, not from the config being hidden.
 */

/** The dev project, committed rather than injected at build time.
 *
 *  Checking these in is the intended use of a Firebase web config, not a
 *  shortcut. The values are handed to every browser that loads the app, so
 *  there is nothing to leak; an ID token is only worth anything because
 *  Google signed it and the API checks that signature. Committing them means
 *  a clone builds and runs without four environment variables having to be
 *  right first, which is a real source of "it works locally".
 *
 *  The environment still wins where it is set, so a second project can be
 *  pointed at without touching this file. */
const DEV_PROJECT = {
  apiKey: "AIzaSyDtCteyWPqnS3cFee4rAC1dasQuVDFD8H8",
  authDomain: "cast-asst-dev.firebaseapp.com",
  projectId: "cast-asst-dev",
  appId: "1:389854372940:web:7d962c10348f2b90ad40e9",
};

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || DEV_PROJECT.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || DEV_PROJECT.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || DEV_PROJECT.projectId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || DEV_PROJECT.appId,
};

/** True when the build was given a Firebase project to talk to. */
export const firebaseConfigured: boolean = Boolean(
  config.apiKey && config.authDomain && config.projectId && config.appId,
);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/**
 * The Auth instance, or null when this build has no Firebase config.
 *
 * Returning null rather than throwing keeps a misconfigured build showing a
 * plain "not configured" message instead of a blank page, which is the
 * difference between a five minute fix and an afternoon.
 */
export function getFirebaseAuth(): Auth | null {
  if (!firebaseConfigured) return null;
  if (!auth) {
    app = initializeApp(config as Required<typeof config>);
    auth = getAuth(app);
    // Survive a refresh and a closed tab. Managers keep this open all day and
    // being signed out by a reload would be read as the product logging them
    // out at random.
    void setPersistence(auth, browserLocalPersistence);
  }
  return auth;
}
